import { DomDocumentHelper, DomWindowHelper } from "survey-core";
import { SurveyCreatorModel } from "survey-creator-core";
import { buildLocator, resolveLocator } from "../journal/journal-locator";
import { getCanvasElement, IPresenceFocus, IPresencePeer, IPresenceState, mapOffset, PRESENCE_SELECTORS, resolveAnchor, resolveEditFocus } from "./presence-state";
import "./presence.scss";

// Above the creator content; below survey-core popups is acceptable (cosmetic).
const OVERLAY_Z_INDEX = 1100;
// A cursor that hasn't moved for this long fades out.
const CURSOR_IDLE_MS = 30_000;
// An offset-mapped cursor point may scroll out of the designer column by
// this much vertically before it is hidden.
const CURSOR_POINT_SLACK = 16;
// A pinned cursor sits this far inside the designer column's edge.
const CURSOR_EDGE_INSET = 2;
// Safety tick for changes no observer catches (animations, scrollIntoView).
const FALLBACK_TICK_MS = 500;
// The peer ring is a 2px (--sjs2-border-width-x200) box-shadow outside the node.
const RING_WIDTH = 2;
// Badge offsets from the ring's OUTER edges (Figma node 102:20059): the
// badge hangs 4px below the ring, its right edge 8px inside the ring's.
const BADGE_GAP = 4;
const BADGE_INSET = 8;

interface IRect { left: number, top: number, width: number, height: number }

const intersects = (a: IRect, b: IRect): boolean =>
  a.left < b.left + b.width && a.left + a.width > b.left &&
  a.top < b.top + b.height && a.top + a.height > b.top;

const containsPoint = (r: IRect, x: number, y: number): boolean =>
  x >= r.left && x <= r.left + r.width && y >= r.top && y <= r.top + r.height;

// A native decoration wanted on a node this tick, plus its name-badge data.
interface IDecoration {
  color: string;
  // Peer name shown on the badge under the ring.
  name: string;
  // Visible area of the node's scroll container - the badge hides outside it.
  clip?: IRect;
  // Overlaying panel rect the badge must not draw over (flyout/mobile sidebar).
  avoid?: IRect;
}

// Per-peer DOM artifacts, created lazily and repositioned every tick.
interface IPeerArtifacts {
  cursor: HTMLElement;
  cursorName: HTMLElement;
  // Receiver-side time of the last observed cursor change (staleness).
  curChangedAt: number;
  // Serialized last cursor - the change detector behind the idle fade.
  lastCurSig: string | undefined;
}

// Renders remote participants.
//
// Element focus (selection ring, focused choice row, property-grid field) is
// drawn NATIVELY: the renderer stamps a `data-collab-focus` attribute plus an
// inline `--collab-peer-color` CSS variable onto the real creator nodes, and
// presence.scss draws the same box-shadow ring the built-in selection uses -
// so it scrolls, clips and layers like the native one. Inline styles and
// unknown attributes survive framework re-renders; a re-CREATED node loses
// them, but the MutationObserver tick below re-applies on the next pass.
//
// Only the mouse cursors and the name badges, which cannot be expressed as
// element decorations, live in a fixed pointer-transparent layer.
export class PresenceOverlay {
  private disposed = false;
  private layer: HTMLElement;
  private artifacts = new Map<string, IPeerArtifacts>();
  // Nodes currently carrying a decoration -> the applied peer color.
  private decorated = new Map<HTMLElement, string>();
  // Name badge (a layer artifact) per decorated node.
  private badges = new Map<HTMLElement, HTMLElement>();
  private rafId: any = 0;
  private scheduled = false;
  private rootPoll: any = 0;
  private fallbackTimer: ReturnType<typeof setInterval>;
  private resizeObserver: ResizeObserver | undefined;
  private mutationObserver: MutationObserver | undefined;
  private doc: Document;

  constructor(private creator: SurveyCreatorModel, private getPeers: () => ReadonlyMap<string, IPresencePeer>) {
    if (!DomDocumentHelper.isAvailable()) return;
    this.doc = DomDocumentHelper.getDocument();
    this.layer = this.doc.createElement("div");
    this.layer.className = "collab-presence-layer";
    this.layer.style.cssText =
      `position:fixed;inset:0;pointer-events:none;z-index:${OVERLAY_Z_INDEX};` +
      "font:11px system-ui,sans-serif;overflow:hidden;";
    const body = DomDocumentHelper.getBody();
    if (!!body) body.appendChild(this.layer);

    this.doc.addEventListener("scroll", this.onScroll, true);
    // Local focus moves decorations (a locally focused translation cell is
    // undecorated) but mutates no observed DOM - repaint on focus changes.
    this.doc.addEventListener("focusin", this.onFocusChange, true);
    this.doc.addEventListener("focusout", this.onFocusChange, true);
    DomWindowHelper.addEventListener("resize", this.onResize);
    this.fallbackTimer = setInterval(() => this.refresh(), FALLBACK_TICK_MS);
    this.rootPoll = this.raf(this.attachObservers);
  }

  // Mark the overlay dirty - repaints on the next animation frame.
  public refresh = (): void => {
    if (this.scheduled || this.disposed || !this.layer) return;
    this.scheduled = true;
    this.rafId = this.raf(() => {
      this.scheduled = false;
      if (!this.disposed)this.render();
    });
  };

  public dispose(): void {
    this.disposed = true;
    if (!this.layer) return;
    this.cancelRaf(this.rafId);
    this.cancelRaf(this.rootPoll);
    clearInterval(this.fallbackTimer);
    this.doc.removeEventListener("scroll", this.onScroll, true);
    this.doc.removeEventListener("focusin", this.onFocusChange, true);
    this.doc.removeEventListener("focusout", this.onFocusChange, true);
    DomWindowHelper.removeEventListener("resize", this.onResize);
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.applyDecorations(new Map());
    this.layer.remove();
  }

  private raf(cb: () => void): any {
    return DomWindowHelper.isAvailable() ? DomWindowHelper.requestAnimationFrame(cb) : setTimeout(cb, 16);
  }
  private cancelRaf(id: any): void {
    const window = DomWindowHelper.getWindow();
    if (!!window) window.cancelAnimationFrame(id);
    else clearTimeout(id);
  }

  private onScroll = (): void => this.refresh();
  private onResize = (): void => this.refresh();
  private onFocusChange = (): void => this.refresh();

  // rootElement appears only after the framework renders the creator.
  private attachObservers = (): void => {
    if (this.disposed) return;
    const root = this.creator.rootElement;
    if (!root) {
      this.rootPoll = this.raf(this.attachObservers);
      return;
    }
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.refresh);
      this.resizeObserver.observe(root);
    }
    if (typeof MutationObserver !== "undefined") {
      this.mutationObserver = new MutationObserver(this.refresh);
      this.mutationObserver.observe(root, { childList: true, subtree: true, attributes: true });
    }
  };

  // --- layer artifacts (cursors only) ------------------------------------------

  private el(className: string, css: string): HTMLElement {
    const node = this.doc.createElement("div");
    node.className = className;
    node.style.cssText = `position:absolute;display:none;${css}`;
    this.layer.appendChild(node);
    return node;
  }

  private getArtifacts(peer: IPresencePeer): IPeerArtifacts {
    let a = this.artifacts.get(peer.clientId);
    if (a) return a;
    const color = peer.color || "#888";
    a = {
      cursor: this.el("collab-presence-cursor", "width:0;height:0;"),
      cursorName: this.el("collab-presence-cursor-name", `padding:2px 7px;border-radius:10px;background:${color};color:#fff;white-space:nowrap;`),
      curChangedAt: 0,
      lastCurSig: undefined
    };
    a.cursor.innerHTML =
      "<svg width=\"16\" height=\"18\" viewBox=\"0 0 16 18\" style=\"display:block\">" +
      "<path d=\"M1 1 L1 14 L4.5 10.8 L7 16.5 L9.3 15.5 L6.8 9.9 L11.5 9.6 Z\" " +
      `fill="${peer.color || "#888"}" stroke="#fff" stroke-width="1"/></svg>`;
    this.artifacts.set(peer.clientId, a);
    return a;
  }

  private dropArtifacts(clientId: string): void {
    const a = this.artifacts.get(clientId);
    if (!a) return;
    for (const node of [a.cursor, a.cursorName]) node.remove();
    this.artifacts.delete(clientId);
  }

  private hide(...nodes: Array<HTMLElement>): void {
    nodes.forEach((n) => { n.style.display = "none"; });
  }
  private place(node: HTMLElement, left: number, top: number): void {
    node.style.display = "block";
    node.style.left = `${Math.round(left)}px`;
    node.style.top = `${Math.round(top)}px`;
  }

  // --- anchor resolution helpers -------------------------------------------------

  private designerContainer(): Element | null {
    return this.doc.querySelector(PRESENCE_SELECTORS.tabContent("designer"));
  }

  // Selection -> the adorner node: direct name anchor, else locator + owner-chain walk.
  private resolveSelectionNode(sel: NonNullable<IPresenceState["sel"]>, container: Element): Element | null {
    const byName = (name: string): Element | null =>
      container.querySelector(PRESENCE_SELECTORS.element(name)) ??
      container.querySelector(PRESENCE_SELECTORS.page(name));
    if (sel.name) {
      const direct = byName(sel.name);
      if (direct) return direct;
    }
    if (!sel.loc) return null; // "" = the survey itself - nothing to outline
    let obj: any;
    try {
      obj = resolveLocator(sel.loc, this.creator.survey);
    } catch{
      return null;
    }
    // Non-anchorable objects (itemvalue, matrix column, ...): climb to the
    // nearest owner that has an adorner in the designer.
    for (let i = 0; obj && i < 20; i++) {
      if (typeof obj.name === "string" && obj.name) {
        const node = byName(obj.name);
        if (node) return node;
      }
      obj = obj.parent ?? obj.parentQuestion ?? obj.colOwner ?? obj.owner ?? null;
    }
    return null;
  }

  // The node that carries the native selection ring for an adorner: the page
  // anchor IS its `.svc-page__content`; a question/panel adorner has the
  // `.svc-question__content` as a direct child, a page wrapper (`.svc-page`,
  // matched by name via its `data-sv-drop-target-survey-element` attribute)
  // has the `.svc-page__content`.
  private ringNode(anchor: Element): Element | null {
    if (anchor.hasAttribute("data-sv-drop-target-survey-page")) return anchor;
    return anchor.querySelector(":scope > .svc-question__content") ??
      anchor.querySelector(":scope > .svc-page__content");
  }

  // tr focus -> the local stringsSurvey matrix name. Matrix names diverge
  // when peers use different showAllStrings/page-filter settings, so identity
  // is established by the owning element's locator + the property name, with
  // the sender's name kept as the same-view fast path. Cached per
  // stringsSurvey instance - the getAllQuestions scan runs once per peer
  // state change, not per rAF tick (the survey is recreated on every
  // locale/filter change, which also invalidates the cache).
  private trMatrixCache: { survey: unknown, byKey: Map<string, string | null> } | null = null;

  private resolveTrMatrixName(tr: Extract<IPresenceFocus, { area: "tr" }>, survey: any): string | null {
    if (this.trMatrixCache?.survey !== survey)this.trMatrixCache = { survey, byKey: new Map() };
    const key = `${tr.loc ?? ""}|${tr.p}|${tr.m}`;
    const cached = this.trMatrixCache.byKey.get(key);
    if (cached !== undefined) return cached;
    let name: string | null = null;
    if (typeof tr.loc === "string") {
      let target: any = null;
      try {
        target = resolveLocator(tr.loc, this.creator.survey);
      } catch{ /* fall through to the name match */ }
      if (target) {
        const hit = survey.getAllQuestions().find((mx: any) => {
          const item = mx.rows?.[0]?.["translationData"];
          return item && item.name === tr.p && item.context === target;
        });
        name = hit?.name ?? null;
      }
    }
    if (!name && survey.getQuestionByName?.(tr.m)) name = tr.m;
    this.trMatrixCache.byKey.set(key, name);
    return name;
  }

  // tr focus -> the cell's td node. The column index comes from the LOCAL
  // matrix columns (peers may show different locale sets); the row-text cell
  // is excluded by the selector, so locale-cell order matches the column
  // order. A locale not visible locally, or a lazily-skipped (skeleton) row,
  // resolves to null - the next tick retries like any other missing anchor.
  private resolveTranslationCell(tr: Extract<IPresenceFocus, { area: "tr" }>, survey: any, container: Element): Element | null {
    const matrixName = this.resolveTrMatrixName(tr, survey);
    const matrix = matrixName ? survey.getQuestionByName(matrixName) : null;
    if (!matrix) return null;
    const colIdx = (matrix.columns ?? []).findIndex((c: any) => c.name === tr.l);
    if (colIdx < 0) return null;
    const row = container.querySelector(PRESENCE_SELECTORS.dataName(matrixName));
    const boxes = row?.querySelectorAll(PRESENCE_SELECTORS.translationCell);
    return boxes && colIdx < boxes.length ? boxes[colIdx] : null;
  }

  // Viewport point for a peer's cursor. "surface" cursors carrying px offsets
  // are mapped from the sender's zoom-normalized canvas box onto the local one
  // (`mapOffset` per axis, rescaled by the local zoom) - the canvas block lays
  // out identically across peers, unlike the window-wide content box the
  // fractions are relative to. Everything else - other scopes, and surface
  // cursors captured while the canvas block had no size - keeps the fraction
  // mapping; null (cursor hidden) when neither encoding is usable locally.
  private cursorPoint(cur: NonNullable<IPresenceState["cur"]>, node: Element):
    { x: number, y: number, byOffset: boolean } | null {
    if (cur.a.s === "surface" &&
      typeof cur.px === "number" && typeof cur.py === "number" &&
      typeof cur.w === "number" && cur.w > 0 && typeof cur.h === "number" && cur.h > 0) {
      const scale = (this.creator.survey?.widthScale || 100) / 100;
      const r = getCanvasElement(node).getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        return {
          x: r.left + mapOffset(cur.px, cur.w, r.width / scale) * scale,
          y: r.top + mapOffset(cur.py, cur.h, r.height / scale) * scale,
          byOffset: true,
        };
      }
    }
    if (typeof cur.x !== "number" || typeof cur.y !== "number") return null;
    const r = node.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return null;
    return { x: r.left + cur.x * r.width, y: r.top + cur.y * r.height, byOffset: false };
  }

  // Fit an offset-mapped cursor point into the designer column (the area
  // between the toolbox and the sidebar). A peer with a wider window has
  // wider gutters around the canvas, so their gutter point can map beyond the
  // local column - onto the toolbox or the sidebar, where it would read as
  // pointing at a panel item. Pin it to the column's edge instead: "just off
  // the canvas at this height" survives the width difference, the lie does
  // not. Vertically the point scrolls with the canvas, so outside the visible
  // column it hides like any scrolled-away anchor. Mutates `p`; returns
  // whether the cursor should be shown.
  private fitSurfacePoint(p: { x: number, y: number }, node: Element, container: IRect): boolean {
    const column = node.closest(PRESENCE_SELECTORS.designerSurface)?.getBoundingClientRect();
    const area = column ?? container;
    if (p.y < area.top - CURSOR_POINT_SLACK || p.y > area.top + area.height + CURSOR_POINT_SLACK) return false;
    p.x = Math.min(Math.max(p.x, area.left + CURSOR_EDGE_INSET), area.left + area.width - CURSOR_EDGE_INSET);
    p.y = Math.min(Math.max(p.y, area.top), area.top + area.height);
    return true;
  }

  // Locator of the locally selected object - gate for property-grid decorations.
  private localSelectionLoc(): string | null {
    if (!this.creator.selectedElement) return null;
    try {
      return buildLocator(this.creator.selectedElement, this.creator.survey);
    } catch{
      return null;
    }
  }

  // --- native decorations (data-collab-focus + --collab-peer-color) -------------

  // Sync the decorated-node set (attributes + name badges) to `wanted`.
  private applyDecorations(wanted: Map<HTMLElement, IDecoration>): void {
    const stale: Array<HTMLElement> = [];
    this.decorated.forEach((_, node) => {
      if (!wanted.has(node)) stale.push(node);
    });
    stale.forEach((node) => {
      node.removeAttribute("data-collab-focus");
      node.style.removeProperty("--collab-peer-color");
      this.decorated.delete(node);
      this.badges.get(node)?.remove();
      this.badges.delete(node);
    });
    wanted.forEach((dec, node) => {
      if (this.decorated.get(node) !== dec.color) {
        node.setAttribute("data-collab-focus", "on");
        node.style.setProperty("--collab-peer-color", dec.color);
        this.decorated.set(node, dec.color);
      }
      this.placeBadge(node, dec);
    });
  }

  // Name badge hugging the ring's bottom-right corner (per the Figma spec).
  // Like the cursors it cannot be expressed as a node decoration, so it lives
  // in the layer and is repositioned from the node's rect every tick.
  private placeBadge(node: HTMLElement, dec: IDecoration): void {
    let badge = this.badges.get(node);
    if (!badge) {
      // Figma: sjs2/typography/x-small-strong on the user color, 4px radius.
      badge = this.el("collab-presence-badge",
        "padding:4px 8px;border-radius:4px;color:#fff;white-space:nowrap;" +
        "font-weight:600;font-size:10px;line-height:14px;" +
        "font-family:var(--sjs2-font-family, system-ui, sans-serif);" +
        "max-width:160px;overflow:hidden;text-overflow:ellipsis;transform:translateX(-100%);");
      this.badges.set(node, badge);
    }
    // An inline string editor draws its visible frame on the border child,
    // INFLATED past the node rect (string-editor.scss: --focus sits at
    // -x050/-x100 offsets) - anchor the badge to that frame, not the node.
    const anchor = node.classList.contains("svc-string-editor")
      ? node.querySelector(".svc-string-editor__border--focus") ?? node
      : node;
    const r = anchor.getBoundingClientRect();
    if (!dec.name || r.width === 0 || (dec.clip && !intersects(r, dec.clip))) {
      this.hide(badge);
      return;
    }
    badge.textContent = dec.name;
    badge.style.background = dec.color;
    badge.style.display = "block"; // measurable before placing
    // On a node narrower than the badge (a checkbox) don't hang out past the
    // ring's left edge - grow rightwards from it instead.
    const rightEdge = Math.max(
      r.left + r.width + RING_WIDTH - BADGE_INSET,
      r.left - RING_WIDTH + badge.offsetWidth);
    const top = r.top + r.height + RING_WIDTH + BADGE_GAP;
    // The badge is a layer artifact, so unlike its ring it would paint over
    // an overlaying sidebar panel - hide it there instead.
    if (dec.avoid && intersects(
      { left: rightEdge - badge.offsetWidth, top, width: badge.offsetWidth, height: badge.offsetHeight },
      dec.avoid)) {
      this.hide(badge);
      return;
    }
    this.place(badge, rightEdge, top);
  }

  // --- per-tick render -----------------------------------------------------------

  private render(): void {
    const peers = this.getPeers();
    const gone: Array<string> = [];
    this.artifacts.forEach((_, id) => {
      if (!peers.has(id)) gone.push(id);
    });
    gone.forEach((id) => this.dropArtifacts(id));

    const localTab = this.creator.activeTab;
    const localTabId = this.creator.activeTabId;
    const localSelLoc = this.localSelectionLoc();
    const now = Date.now();
    const sidebar = this.doc.querySelector(PRESENCE_SELECTORS.sidebar);
    // In flyout/mobile mode the .svc-side-bar box shrinks to the tabs strip
    // while the visible panel is positioned absolutely outside of it (see
    // side-bar.scss) - measure the panel, not the host box.
    const sidebarRect = (sidebar?.querySelector(PRESENCE_SELECTORS.sidebarPanel) ?? sidebar)?.getBoundingClientRect();
    // In flyout/mobile mode the panel floats OVER the tab content (z-index
    // 1000, side-bar.scss) while the underlying column keeps its full layout
    // width, so neither the column clamp nor the anchor-rect test notices it -
    // and the fixed layer (z-index 1100) paints over the panel. Cursor points
    // and badges landing under it must hide instead of drawing on top. No
    // `?? sidebar` fallback here: a collapsed flyout renders no container and
    // must not occlude. (If the expanded toolbox ever needs the same
    // treatment, generalize to an occluders[] list.)
    const panel = sidebar?.matches(PRESENCE_SELECTORS.sidebarOverlaying)
      ? sidebar.querySelector(PRESENCE_SELECTORS.sidebarPanel)?.getBoundingClientRect()
      : undefined;
    const occluder: IRect | undefined = panel && panel.width > 0 && panel.height > 0 ? panel : undefined;
    const designer = localTab === "designer" ? this.designerContainer() : null;
    const designerRect = designer?.getBoundingClientRect();
    const translation = localTab === "translation"
      ? this.doc.querySelector(PRESENCE_SELECTORS.tabContent("translation")) : null;
    const translationRect = translation?.getBoundingClientRect();
    const stringsSurvey = translation
      ? (this.creator.getPlugin("translation", false) as any)?.model?.stringsSurvey : null;
    // Desired decorations this tick; first peer to claim a node wins.
    const wanted = new Map<HTMLElement, IDecoration>();

    peers.forEach((peer) => {
      const a = this.getArtifacts(peer);
      const state = peer.state;
      const color = peer.color || "#888";

      // 1) element focus ring - decorate the real node; independently, a
      // focused inline string editor lights up its native focus border
      // (mirrors what the peer sees locally: selection ring + editor border).
      // A non-null channel implies the peer's tab renders it (the capture
      // clears every channel atomically with a tab switch), so rendering is
      // gated on the LOCAL view only.
      if (designer) {
        if (state.sel) {
          const anchor = this.resolveSelectionNode(state.sel, designer);
          const target = anchor ? this.ringNode(anchor) : null;
          if (target instanceof HTMLElement && !wanted.has(target)) {
            wanted.set(target, { color, name: peer.name, clip: designerRect, avoid: occluder });
          }
        }
        if (state.focus?.area === "edit") {
          const editor = resolveEditFocus(state.focus, designer);
          if (editor instanceof HTMLElement && !wanted.has(editor)) {
            wanted.set(editor, { color, name: peer.name, clip: designerRect, avoid: occluder });
          }
        }
      }

      // 2) property-grid field ring - only when the local grid shows the same
      // content. The grid flavor derives from the peer's tab (focus dies with
      // every tab switch, so they cannot disagree); the main grid always
      // shows the SELECTED object, so "same content" means the peer's
      // selection is the local one.
      if (state.focus?.area === "pg" && sidebar) {
        const sameContent = state.tab === "theme"
          ? localTab === "theme"
          : localTab === "designer" && !!state.sel && state.sel.loc === localSelLoc;
        if (sameContent) {
          const field = sidebar.querySelector(PRESENCE_SELECTORS.dataName(state.focus.prop));
          // The [data-name] node spans the whole row incl. its title; the
          // native focus only outlines the input area, so decorate the
          // question content when present. A boolean row's content spans the
          // checkbox AND its label - ring just the checkbox itself.
          const target = (field?.classList.contains(PRESENCE_SELECTORS.pgBooleanRow)
            ? field.querySelector(PRESENCE_SELECTORS.pgCheckbox) : null) ??
            field?.querySelector(PRESENCE_SELECTORS.pgQuestionContent) ?? field;
          if (target instanceof HTMLElement && !wanted.has(target)) {
            wanted.set(target, { color, name: peer.name, clip: sidebarRect });
          }
        }
      }

      // 2b) translation-cell ring. A cell the local user is editing is not
      // decorated at all - the ring is also killed instantly by the
      // :focus-within guard in the CSS, and skipping here removes the badge
      // with it (a badge under a ringless cell reads as a broken highlight).
      if (state.focus?.area === "tr" && translation && stringsSurvey) {
        const cell = this.resolveTranslationCell(state.focus, stringsSurvey, translation);
        const locallyFocused = !!cell && cell.contains(this.doc.activeElement);
        if (cell instanceof HTMLElement && !locallyFocused && !wanted.has(cell)) {
          wanted.set(cell, { color, name: peer.name, clip: translationRect, avoid: occluder });
        }
      }

      // 3) cursor - same view only (tab strip cursors are global), idle fade.
      // Movement is detected by content: the sender dedupes identical
      // consecutive cursors, so changed content IS a move, while unchanged
      // content only re-arrives when some other state field was resent and
      // must not reset the fade.
      const cur = state.cur;
      const curSig = cur ? JSON.stringify(cur) : undefined;
      if (curSig !== a.lastCurSig) {
        a.lastCurSig = curSig;
        if (cur) a.curChangedAt = now;
      }
      let cursorShown = false;
      // After the same-view gate the peer's anchors live in the LOCAL active
      // tab's container, so the DOM key is the local activeTabId - it never
      // travels on the wire.
      if (cur && now - a.curChangedAt < CURSOR_IDLE_MS &&
        (cur.a.s === "tabbar" || state.tab === localTab)) {
        const node = resolveAnchor(cur.a, localTabId, this.doc);
        if (node) {
          const container = cur.a.s === "pg" ? sidebarRect
            : cur.a.s === "tabbar" ? undefined
              : this.doc.querySelector(PRESENCE_SELECTORS.tabContent(localTabId))?.getBoundingClientRect();
          const p = this.cursorPoint(cur, node);
          // Offset-mapped points are fitted themselves (their anchor - the
          // whole canvas - is always partly visible, so an anchor-rect test
          // would keep cursors shown over the toolbox after a scroll);
          // fraction points keep the anchor-rect visibility test.
          let visible = !!p;
          if (p && container !== undefined) {
            visible = !container ? false
              : p.byOffset ? this.fitSurfacePoint(p, node, container)
                : intersects(node.getBoundingClientRect(), container);
            // pg cursors target the panel itself and must keep drawing over it.
            if (visible && occluder && cur.a.s !== "pg") {
              visible = !containsPoint(occluder, p.x, p.y);
            }
          }
          if (p && visible) {
            this.place(a.cursor, p.x, p.y);
            this.place(a.cursorName, p.x + 12, p.y + 14);
            a.cursorName.textContent = peer.name;
            cursorShown = true;
          }
        }
      }
      if (!cursorShown)this.hide(a.cursor, a.cursorName);
    });

    this.applyDecorations(wanted);
  }
}
